import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../store/useAppStore';
import { qk } from '../services/queryKeys';
import {
  fetchEmployees, fetchEmployee, fetchMyEmployee, fetchDepartments, fetchLeaveRequests, fetchLeaveBalances,
  fetchLeaveTypes, fetchAttendance, fetchEmployeeDocuments,
  fetchPayrollRuns, fetchPayslips, fetchIssuedItems,
  updateEmployeeApi, resolveAvatarSrc,
} from '../services/api';
import Avatar from '../components/Avatar';
import Chip from '../components/Chip';
import Button from '../components/Button';
import ProgressBar from '../components/ProgressBar';
import Icon from '../components/Icon';
import Alert from '../components/Alert';
import Modal from '../components/Modal';
import AvatarViewerModal from '../components/AvatarViewerModal';
import IdentifiersPanel from '../components/IdentifiersPanel';
import SignInAccessPanel from '../components/SignInAccessPanel';
import type { ProfileTab } from '../types';
import { formatMinor, sumMinor, toMinor } from '../lib/money';

const TABS: { key: ProfileTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'leave', label: 'Leave' },
  { key: 'attendance', label: 'Attendance' },
  { key: 'documents', label: 'Documents' },
  { key: 'compensation', label: 'Compensation' },
];

const STATUS_VARIANT: Record<string, 'success' | 'warn' | 'neutral'> = {
  active: 'success', on_leave: 'warn', separated: 'neutral',
};

function InfoRow({ label, value }: { label: string; value?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink)', fontWeight: 600, textAlign: 'right', marginLeft: 16 }}>{value ?? '—'}</span>
    </div>
  );
}

function SectionHead({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-4, #9ca3af)', paddingTop: 20, paddingBottom: 2 }}>
      {children}
    </div>
  );
}

function fmtDate(iso?: string | null) {
  if (!iso) return undefined;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function Profile() {
  const { employeeId: urlEmployeeId } = useParams<{ employeeId?: string }>();
  const navigate = useNavigate();
  const { profileId, employeeId, email, profileTab, role, setProfileTab, openModal, modal, closeModal, toast } = useAppStore();
  const queryClient = useQueryClient();

  const { data: employees = [] } = useQuery({ queryKey: qk.employees(), queryFn: fetchEmployees });
  const { data: departments = [] } = useQuery({ queryKey: qk.departments(), queryFn: fetchDepartments });

  // /employees/me — works for all roles; used as fast path for ESS own-profile
  const { data: myEmpData } = useQuery({
    queryKey: ['employees', 'me'],
    queryFn: fetchMyEmployee,
    enabled: !urlEmployeeId,
    retry: 1,
  });

  // Resolve subject employee — prefer resolved DB UUID over platform user ID
  const me = myEmpData
    ?? employees.find((e) => e.id === employeeId)
    ?? employees.find((e) => e.email === email)
    ?? employees.find((e) => e.platformUserId === profileId);
  const subjectId = urlEmployeeId || me?.id || employeeId;

  // Direct fetch by ID — after backend fix, also works for ESS own record
  const { data: singleEmp } = useQuery({
    queryKey: qk.employee(subjectId ?? ''),
    queryFn: () => fetchEmployee(subjectId!),
    enabled: !!subjectId && (!!urlEmployeeId || !myEmpData),
    retry: 1,
  });
  const emp = (!urlEmployeeId ? myEmpData : undefined)
    ?? employees.find((e) => e.id === subjectId)
    ?? singleEmp;

  const { data: leaveRequests = [] } = useQuery({
    queryKey: qk.leaveRequests(subjectId),
    queryFn: () => fetchLeaveRequests(subjectId),
    enabled: !!subjectId,
  });

  const { data: leaveBalances = [] } = useQuery({
    queryKey: qk.leaveBalances(subjectId),
    queryFn: () => fetchLeaveBalances(subjectId),
    enabled: !!subjectId,
  });

  const { data: leaveTypes = [] } = useQuery({ queryKey: qk.leaveTypes(), queryFn: fetchLeaveTypes });

  const { data: attendanceData = [] } = useQuery({
    queryKey: qk.attendance(subjectId),
    queryFn: () => fetchAttendance(subjectId),
    enabled: !!subjectId,
  });

  const { data: documents = [] } = useQuery({
    queryKey: qk.documents(subjectId),
    queryFn: () => fetchEmployeeDocuments(subjectId),
    enabled: !!subjectId,
  });

  const { data: issuedItems = [] } = useQuery({
    queryKey: qk.issuedItems(subjectId ?? ''),
    queryFn: () => fetchIssuedItems(subjectId!),
    enabled: !!subjectId,
    retry: false,
  });

  const compensationAllowed = role === 'hr' || (role === 'ess' && subjectId === profileId);

  const { data: payrollRuns = [] } = useQuery({
    queryKey: qk.payrollRuns(),
    queryFn: fetchPayrollRuns,
    enabled: compensationAllowed,
  });
  const latestRunId = payrollRuns[0]?.id;
  const { data: allPayslips = [] } = useQuery({
    queryKey: qk.payslips(latestRunId ?? ''),
    queryFn: () => fetchPayslips(latestRunId!),
    enabled: !!latestRunId && compensationAllowed,
  });
  const myLatestSlip = allPayslips.find(p => p.employeeId === subjectId);

  // Basic info, work info, and hierarchy are HR-owned system-of-record data —
  // only HR can edit them (department/designation/location/manager changes
  // go through the org chart's placement flow, not a plain field edit).
  // No self-service edit at all: name/email/phone are set at signup and
  // locked from then on, even for the employee they belong to — HR is the
  // only party that can ever change them. (There used to be a self-service
  // phone-number path here; removed on purpose, not an oversight — leaving
  // the backend endpoint reachable would have let anyone override their own
  // record via a direct API call even with the UI button gone, so that
  // capability was removed from sk-people-api too, not just hidden here.)
  const myId = employeeId || profileId;
  const isOwnProfile = !!subjectId && subjectId === myId;
  const [showAvatarViewer, setShowAvatarViewer] = useState(false);
  const canEdit = role === 'hr';
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });

  const updateHrMutation = useMutation({
    mutationFn: (input: { firstName: string; lastName: string; personalEmail: string; personalPhone: string }) =>
      updateEmployeeApi(subjectId!, input),
    onSuccess: (updated) => {
      // HR editing their OWN profile also needs the 'me' cache slot updated
      // directly — see the ClockCard.tsx avatar-upload fix for why
      // invalidateQueries alone can't refresh it.
      if (isOwnProfile) queryClient.setQueryData(['employees', 'me'], updated);
      queryClient.invalidateQueries({ queryKey: qk.employees() });
      queryClient.invalidateQueries({ queryKey: qk.employee(subjectId!) });
      toast('Profile updated');
      closeModal();
    },
    onError: () => toast('Failed to update profile', 'danger'),
  });

  if (!emp) return <div style={{ padding: 48, color: 'var(--ink-3)' }}>Employee not found</div>;

  const dept = departments.find((d) => d.id === emp.departmentId);
  const manager = employees.find((e) => e.id === emp.managerId);
  const directReports = employees.filter((e) => e.managerId === subjectId);
  const myAttendance = attendanceData.slice(0, 20);
  const myDocs = documents.filter((d) => d.employeeId === subjectId);

  // Balance cards: join leaveTypes with balances
  const balances = leaveTypes.map((lt) => ({
    ...lt,
    leaveTypeId: lt.id,
    balance: Number(leaveBalances.find((b) => b.leaveTypeId === lt.id)?.balance ?? 0),
    credited: leaveBalances.find((b) => b.leaveTypeId === lt.id)?.credited ?? 0,
    debited: leaveBalances.find((b) => b.leaveTypeId === lt.id)?.debited ?? 0,
  }));

  return (
    <div>
      {/* Back button — only shown when browsing from Directory */}
      {urlEmployeeId && (
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--ink-2)', fontSize: 'var(--fs-sm)', fontWeight: 500,
            padding: '0 0 16px 0',
          }}
        >
          <span style={{ display: 'flex', transform: 'rotate(180deg)' }}>
            <Icon name="chevron_right" size={14} color="var(--ink-2)" />
          </span>
          Back to directory
        </button>
      )}

      {/* Header */}
      <div className="t-card" style={{ marginBottom: 20 }}>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
            {isOwnProfile && emp.avatarUrl ? (
              <button
                type="button"
                title="View profile picture"
                onClick={() => setShowAvatarViewer(true)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', borderRadius: '50%', flexShrink: 0 }}
              >
                <Avatar firstName={emp.firstName} lastName={emp.lastName} src={resolveAvatarSrc(emp.avatarUrl)} color={emp.avatarColor} size="xl" />
              </button>
            ) : (
              <Avatar firstName={emp.firstName} lastName={emp.lastName} src={resolveAvatarSrc(emp.avatarUrl)} color={emp.avatarColor} size="xl" />
            )}
            <div style={{ flex: 1 }}>
              {canEdit && (
                <div style={{ float: 'right' }}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditForm({ firstName: emp.firstName, lastName: emp.lastName ?? '', email: emp.email ?? '', phone: emp.phone ?? '' });
                      openModal('edit_profile');
                    }}
                  >
                    <Icon name="edit" size={13} color="currentColor" /> Edit
                  </Button>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <h2 style={{ fontSize: 'var(--fs-xl)', fontWeight: 600 }}>{emp.firstName} {emp.lastName}</h2>
                <Chip variant={STATUS_VARIANT[emp.status]} dot>{emp.status.replace('_', ' ')}</Chip>
                <Chip variant="neutral">{emp.employmentType.replace('_', ' ')}</Chip>
              </div>
              <p style={{ color: 'var(--ink-2)', fontSize: 'var(--fs-base)' }}>{emp.jobTitle}</p>
              <p style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)', marginTop: 2 }}>{dept?.name ?? '—'} · {emp.employeeCode}</p>
              {(emp.platformUserId || emp.crmContactId) && (
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  {emp.platformUserId && <Chip variant="info">Platform seat</Chip>}
                  {emp.crmContactId && <Chip variant="accent">CRM contact</Chip>}
                </div>
              )}
              <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
                {emp.email && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>
                    <Icon name="mail" size={13} color="var(--ink-3)" />{emp.email}
                  </div>
                )}
                {emp.phone && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>
                    <Icon name="phone" size={13} color="var(--ink-3)" />{emp.phone}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/*

        * CH-U3 — identifiers with expiry warn states. Shown for every employee

        * record; only HR may record or renew one, matching the HR-only rule on

        * every other system-of-record field.

        */}

      {/* Invite to sign in — HR only, and only while the record has no login. */}
      <SignInAccessPanel employee={emp} canEdit={role === 'hr'} />

      {subjectId && <IdentifiersPanel employeeId={subjectId} canEdit={role === 'hr'} />}


      {/* Tabs */}
      <div className="t-tabs">
        {TABS.map((t) => (
          <button key={t.key} className={`t-tab${profileTab === t.key ? ' active' : ''}`} onClick={() => setProfileTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {profileTab === 'overview' && (
        <div className="two-col" style={{ alignItems: 'start' }}>
          {/* Left — Contact + Issued items */}
          <div>
            <SectionHead>Contact</SectionHead>
            <InfoRow label="Work email" value={emp.email} />
            <InfoRow label="Phone" value={emp.phone} />
            <InfoRow label="Location" value={emp.workLocation} />
            <InfoRow label="Employee ID" value={emp.employeeCode} />

            <SectionHead>Issued items</SectionHead>
            {issuedItems.length === 0 ? (
              <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', padding: '10px 0' }}>No items issued.</p>
            ) : (
              issuedItems.map((item) => (
                <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink)' }}>{item.label}</span>
                  {item.returnedOn
                    ? <Chip variant="neutral">returned</Chip>
                    : <Chip variant="success">issued</Chip>}
                </div>
              ))
            )}
          </div>

          {/* Right — Employment + Direct reports */}
          <div>
            <SectionHead>Employment</SectionHead>
            <InfoRow label="Department" value={dept?.name} />
            <InfoRow label="Designation" value={emp.jobTitle} />
            <InfoRow label="Type" value={emp.employmentType.replace('_', ' ')} />
            <InfoRow label="Reports to" value={manager ? `${manager.firstName} ${manager.lastName}` : undefined} />
            <InfoRow label="Joined" value={fmtDate(emp.startDate)} />
            {emp.confirmationDate && <InfoRow label="Confirmed" value={fmtDate(emp.confirmationDate)} />}

            <SectionHead>Direct reports</SectionHead>
            {directReports.length === 0 ? (
              <div style={{ border: '1.5px dashed var(--border)', borderRadius: 8, padding: '20px 16px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 'var(--fs-sm)', marginTop: 8 }}>
                No direct reports.
              </div>
            ) : (
              directReports.map((r) => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <Avatar firstName={r.firstName} lastName={r.lastName} src={resolveAvatarSrc(r.avatarUrl)} color={r.avatarColor} size="sm" />
                  <div>
                    <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--ink)' }}>{r.firstName} {r.lastName}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{r.jobTitle}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Leave */}
      {profileTab === 'leave' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
            {balances.map((b) => (
              <div key={b.leaveTypeId} className="leave-bal-card">
                <div className="leave-bal-name">{b.name}</div>
                <div className="leave-bal-count">{b.balance}</div>
                <div className="leave-bal-days">of {b.annualDays} days</div>
                <ProgressBar value={b.balance} max={b.annualDays} variant={b.balance < 3 ? 'warn' : 'accent'} />
              </div>
            ))}
          </div>
          <div className="t-card">
            <div className="card-head"><h3>Leave history</h3></div>
            <table className="t-table">
              <thead><tr><th>Type</th><th>Dates</th><th>Days</th><th>Status</th></tr></thead>
              <tbody>
                {leaveRequests.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--ink-3)' }}>No leave requests</td></tr>
                ) : leaveRequests.map((r) => {
                  const lt = leaveTypes.find(l => l.id === r.leaveTypeId);
                  return (
                    <tr key={r.id}>
                      <td>{lt?.name}</td>
                      <td>{r.startDate} – {r.endDate}</td>
                      <td>{r.days}</td>
                      <td><Chip variant={r.status === 'approved' ? 'success' : r.status === 'submitted' ? 'warn' : r.status === 'rejected' ? 'danger' : 'neutral'} dot>{r.status}</Chip></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Attendance */}
      {profileTab === 'attendance' && (
        <div className="t-card">
          <div className="card-head"><h3>Attendance ledger</h3></div>
          <table className="t-table">
            <thead><tr><th>Date</th><th>Type</th><th>Time</th><th>Note</th></tr></thead>
            <tbody>
              {myAttendance.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--ink-3)' }}>No records</td></tr>
              ) : myAttendance.map((a) => (
                <tr key={a.id}>
                  <td>{a.punchedAt.slice(0, 10)}</td>
                  <td><Chip variant={a.punchType === 'clock_in' ? 'success' : 'neutral'}>{a.punchType.replace('_', ' ')}</Chip></td>
                  <td className="mono">{new Date(a.punchedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</td>
                  <td style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>{a.source ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Documents */}
      {profileTab === 'documents' && (
        <div className="t-card">
          <div className="card-head"><h3>Documents</h3></div>
          {myDocs.length === 0 ? (
            <div className="card-body" style={{ color: 'var(--ink-3)', textAlign: 'center' }}>No documents</div>
          ) : (
            <div>
              {myDocs.map((d) => (
                <div key={d.id} className="sk-listrow">
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name="doc" size={16} color="var(--accent)" />
                  </div>
                  <div className="row-main">
                    <div className="row-title">{d.title}</div>
                    <div className="row-sub">{d.category} · {d.version}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                    {d.status === 'expired' && <Chip variant="danger">Expired</Chip>}
                    {d.requiresAck && !d.ackedAt && (
                      <Button variant="outline" size="sm" onClick={() => openModal('acknowledge_doc', { docId: d.id })}>
                        Acknowledge
                      </Button>
                    )}
                    {d.requiresAck && d.ackedAt && <Chip variant="success">Acknowledged</Chip>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Compensation */}
      {profileTab === 'compensation' && (
        compensationAllowed ? (
          myLatestSlip ? (() => {
            // Minor-unit strings (CR-1): format at the edge, sum in integers.
            const fmtINR = (minor: string | number | null | undefined) =>
              formatMinor(minor, myLatestSlip.currencyCode ?? 'INR', { maximumFractionDigits: 0 });
            const earnings = (myLatestSlip.lines ?? []).filter(l => !l.isDeduction);
            const grossMonthly =
              sumMinor(earnings.map(l => l.amountMinor)) || toMinor(myLatestSlip.grossPayMinor);
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Salary structure — earnings components */}
                <div className="t-card">
                  <div className="card-head">
                    <h3>Salary structure</h3>
                  </div>
                  <table className="t-table">
                    <thead><tr><th>Component</th><th style={{ textAlign: 'right' }}>Monthly</th></tr></thead>
                    <tbody>
                      {earnings.length > 0 ? earnings.map((l) => (
                        <tr key={l.id}>
                          <td>{l.label}</td>
                          <td className="mono" style={{ textAlign: 'right' }}>{fmtINR(l.amountMinor)}</td>
                        </tr>
                      )) : (
                        <>
                          <tr><td>Basic</td><td className="mono" style={{ textAlign: 'right' }}>—</td></tr>
                          <tr><td>HRA</td><td className="mono" style={{ textAlign: 'right' }}>—</td></tr>
                          <tr><td>Special Allowance</td><td className="mono" style={{ textAlign: 'right' }}>—</td></tr>
                        </>
                      )}
                      <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                        <td>Gross monthly</td>
                        <td className="mono" style={{ textAlign: 'right', color: 'var(--ink)' }}>{fmtINR(grossMonthly)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Latest payslip mini-card */}
                <div className="t-card">
                  <div className="card-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3>Latest payslip</h3>
                    <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>{myLatestSlip.periodLabel}</span>
                  </div>
                  <div className="card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginBottom: 4 }}>Net pay</div>
                      <div style={{ fontSize: 'var(--fs-2xl, 28px)', fontWeight: 700, color: 'var(--success)', letterSpacing: '-0.5px' }}>
                        {fmtINR(myLatestSlip.netPayMinor)}
                      </div>
                    </div>
                    <Button variant="accent" onClick={() => navigate('/payroll')}>
                      <Icon name="eye" size={13} color="white" />
                      View payslip
                    </Button>
                  </div>
                </div>
              </div>
            );
          })() : (
            <Alert variant="info">No payslip data available yet. Salary structure will appear here after the first payroll run.</Alert>
          )
        ) : (
          <Alert variant="warn">You don't have permission to view compensation details for this employee.</Alert>
        )
      )}

      {/* Edit profile — HR only. Employee ID, department, designation,
          location, employment type, status, and reporting manager are never
          editable here (server-generated or owned by the org-chart/placement
          flow instead). No self-service path at all — see the canEdit comment
          above for why. */}
      {modal.type === 'edit_profile' && (
        <Modal
          title="Edit profile"
          onClose={closeModal}
          footer={
            <>
              <Button variant="ghost" onClick={closeModal} disabled={updateHrMutation.isPending}>Cancel</Button>
              <Button
                variant="accent"
                disabled={updateHrMutation.isPending}
                onClick={() => {
                  if (!editForm.firstName.trim()) { toast('First name is required', 'warn'); return; }
                  updateHrMutation.mutate({
                    firstName: editForm.firstName.trim(),
                    lastName: editForm.lastName.trim(),
                    personalEmail: editForm.email.trim(),
                    personalPhone: editForm.phone.trim(),
                  });
                }}
              >
                {updateHrMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>First name</label>
              <input className="t-input" value={editForm.firstName} onChange={(e) => setEditForm((f) => ({ ...f, firstName: e.target.value }))} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>Last name</label>
              <input className="t-input" value={editForm.lastName} onChange={(e) => setEditForm((f) => ({ ...f, lastName: e.target.value }))} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>Email</label>
              <input className="t-input" type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>Phone</label>
              <input className="t-input" value={editForm.phone} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} />
            </div>
          </div>
        </Modal>
      )}

      {showAvatarViewer && emp.avatarUrl && (
        <AvatarViewerModal
          firstName={emp.firstName}
          lastName={emp.lastName}
          src={resolveAvatarSrc(emp.avatarUrl) as string}
          onClose={() => setShowAvatarViewer(false)}
        />
      )}
    </div>
  );
}
